<script setup lang="ts">
import { onMounted, ref, watchEffect } from "vue";
import Lucide from "@/components/Base/Lucide";
import logoUrl from "@/assets/images/cfm-logo.png";
import profileUrl from "@/assets/images/profile.png";
import Breadcrumb from "@/components/Base/Breadcrumb";
import { Menu } from "@/components/Base/Headless";
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import mozFlagUrl from "@/assets/images/moz-flag.png";
import engFlagUrl from "@/assets/images/eng-flag.png";
import { useI18n } from 'vue-i18n';
import { useUserProfile } from '@/composables/user-profile'; 

const store = useStore();
const user = ref(null);
const role = ref(null);
const { t, locale } = useI18n();
const router = useRouter();
const alertMessage = ref<{ variant: string; message: string } | null>(null);

const { fetchUserProfile, updateUserLocale, isProcessing } = useUserProfile();
const isPortuguese = ref(locale.value === 'pt');

// Fetch user profile on component mount and set default language
onMounted(async () => {
  try {
    const userData = await fetchUserProfile();
    
    // Set the toggle state based on fetched locale
    if (userData && userData.locale) {
      isPortuguese.value = userData.locale === 'pt';
      // The locale is already set inside fetchUserProfile
    }
  } catch (error) {
    console.error('Failed to load user profile:', error);
    // Fallback to localStorage or default 'en'
    const savedLocale = localStorage.getItem('locale') || 'en';
    locale.value = savedLocale;
    isPortuguese.value = savedLocale === 'pt';
  }
});

const toggleLocale = async () => {
  const newLocale = isPortuguese.value ? 'en' : 'pt';

  try {
    await updateUserLocale(newLocale);
    isPortuguese.value = !isPortuguese.value;

    alertMessage.value = {
      variant: 'soft-success',
      message: newLocale === 'en'
        ? 'Language changed to English'
        : 'Idioma alterado para Português'
    };

    setTimeout(() => alertMessage.value = null, 3000);
  } catch (error) {
    alertMessage.value = {
      variant: 'soft-danger',
      message: 'Failed to update language preference'
    };
    setTimeout(() => alertMessage.value = null, 3000);
  }
};

const handleLogout = async () => {
  try {
    await store.dispatch('auth/logout', store.state.auth.token);
    alertMessage.value = { variant: 'soft-success', message: "Logout successfully" };
    await router.replace({ name: 'login' });
  } catch (error) {
    if (error.message) {
      alertMessage.value = { variant: 'soft-danger', message: 'An error occurred while logging out.' };
    }
  }
};

const profilePage = async () => {
  alertMessage.value = null;
  try {
    await router.replace({ name: 'profile' });
  } catch (error) {
    alertMessage.value = { variant: 'soft-danger', message: error.message };
  }
};

const props = defineProps<{
  layout?: "side-menu" | "simple-menu" | "top-menu";
}>();

watchEffect(() => {
  user.value = store.state.auth.user;
  role.value = store.state.auth.role;
});
</script>

<template>
  <div
    :class="[
      'h-[70px] md:h-[20px] z-[51] border-b border-white/[0.08] mt-12 md:mt-0 -mx-3 sm:-mx-8 md:-mx-0 px-3 md:border-b-0 relative md:fixed md:inset-x-0 md:top-0 sm:px-8 md:px-10 md:pt-10 md:bg-gradient-to-b md:from-slate-100 md:to-transparent dark:md:from-darkmode-700',
      props.layout == 'top-menu' && 'dark:md:from-darkmode-800',
      'before:content-[\'\'] before:absolute before:h-[85px] before:inset-0 before:top-0 before:mx-7 before:bg-primary/30 before:mt-0  before:hidden before:md:block before:dark:bg-darkmode-600/30',
      'after:content-[\'\'] after:absolute after:inset-0 after:h-[85px] after:mx-3 after:bg-primary after:mt-0 after:shadow-md after:hidden after:md:block after:dark:bg-darkmode-600',
    ]"
  >
    <div class="flex items-center h-full">
      <!-- BEGIN: Logo -->
      <RouterLink
        :to="{ name: 'home' }"
        :class="[
          '-intro-x hidden md:flex',
          props.layout == 'side-menu' && 'xl:w-[180px]',
          props.layout == 'simple-menu' && 'xl:w-auto',
          props.layout == 'top-menu' && 'w-auto',
        ]"
      >
        <img
          alt="CFM"
          class="intro-x bg-white p-3 w-28 rounded"
          :src="logoUrl"
        />
      </RouterLink>
      <!-- END: Logo -->
      
      <!-- BEGIN: Breadcrumb -->
      <Breadcrumb
        light
        :class="['h-[45px] md:ml-10 md:border-l border-white/[0.08] dark:border-white/[0.08] mr-auto -intro-x',props.layout != 'top-menu' && 'md:pl-6',props.layout == 'top-menu' && 'md:pl-10',]">
      </Breadcrumb>
      
      <!-- BEGIN: Language Toggle -->
      <div class="mr-4 intro-x sm:mr-6 p-1">
        <div class="flex justify-round">
          <img
            alt="MozFlag"
            class="w-6 h-6"
            :src="mozFlagUrl"
          />
          <label class="inline-flex items-center cursor-pointer px-2">
            <input 
              type="checkbox" 
              class="sr-only peer" 
              :checked="!isPortuguese"
              :disabled="isProcessing"
              @change="toggleLocale"
            >
            <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
          <img
            alt="EngFlag"
            class="w-6 h-6"
            :src="engFlagUrl"
          />
        </div>
      </div>
      <!-- END: Language Toggle -->
      
      <!-- BEGIN: Account Menu -->
      <Menu>
        <Menu.Button class="block w-8 h-8 overflow-hidden rounded-full shadow-lg image-fit zoom-in intro-x">
          <img class="bg-white p-1" alt="CFM" :src="profileUrl"/>
        </Menu.Button>
        <Menu.Items class="w-56 mt-px relative bg-primary/80 before:block before:absolute before:bg-black before:inset-0 before:rounded-md before:z-[-1] text-white">
          <Menu.Header class="font-normal">
            <div class="font-medium">{{ user?.first_name ?? "N/A" }} {{ user?.last_name ?? "N/A" }}</div>
            <div class="text-xs text-gray-300 italic">{{ role?.name ?? "N/A" }}</div>
          </Menu.Header>
          <Menu.Item class="hover:bg-white/5" @click="profilePage">
            <Lucide icon="Lock" class="w-4 h-4 mr-2" /> {{$t("message.profile")}}
          </Menu.Item>
          <Menu.Divider class="bg-white/[0.08]" />
          <Menu.Item class="hover:bg-white/5" @click="handleLogout">
            <Lucide icon="ToggleRight" class="w-4 h-4 mr-2 capitalize" />
            <span class="capitalize">{{ $t("message.logout")}}</span>
          </Menu.Item>
        </Menu.Items>
      </Menu>
      <!-- END: Account Menu -->
      
      <!-- Alert Message -->
      <div v-if="alertMessage" class="fixed top-20 right-4 z-50">
        <div :class="`alert alert-${alertMessage.variant} show`">
          {{ alertMessage.message }}
        </div>
      </div>
    </div>
  </div>
</template>